import React, { useEffect, useState } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import { Menu, X, LogIn, LogOut, ShieldCheck, Radio, CalendarDays, GraduationCap, IdCard, ShoppingBag, Home as HomeIcon, Facebook, Youtube, Music2, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const NAV = [
  { to: "/", label: "Home", icon: HomeIcon, end: true },
  { to: "/eventi", label: "Eventi", icon: CalendarDays },
  { to: "/podcast", label: "Podcast", icon: Radio },
  { to: "/tesseramento", label: "Tesseramento", icon: IdCard },
  { to: "/corsi", label: "Corsi", icon: GraduationCap },
  { to: "/shop", label: "Shop", icon: ShoppingBag },
  { to: "/contatti", label: "Contatti", icon: Mail },
];

// Instagram icon in its original brand gradient (purple → pink → orange → yellow)
const InstagramColorIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <defs>
      <radialGradient id="ig-grad" cx="0.3" cy="1.1" r="1.2">
        <stop offset="0" stopColor="#FDF497"/>
        <stop offset="0.05" stopColor="#FDF497"/>
        <stop offset="0.45" stopColor="#FD5949"/>
        <stop offset="0.6" stopColor="#D6249F"/>
        <stop offset="0.9" stopColor="#285AEB"/>
      </radialGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#ig-grad)"/>
    <path d="M12 7.5A4.5 4.5 0 1 0 12 16.5A4.5 4.5 0 1 0 12 7.5Z" fill="none" stroke="#fff" strokeWidth="1.6"/>
    <circle cx="17.4" cy="6.6" r="1.05" fill="#fff"/>
  </svg>
);

// Simple X (Twitter) glyph, since lucide's Twitter icon is deprecated
const XIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2H21.5l-7.51 8.583L23 22h-6.834l-5.36-6.99L4.6 22H1.343l8.033-9.183L1 2h6.998l4.85 6.4L18.244 2Zm-1.2 18h1.86L7.03 4H5.06l11.984 16Z"/>
  </svg>
);
const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.85 20.9a6.28 6.28 0 0 0 10.36-4.85V9.83a8.16 8.16 0 0 0 4.85 1.63V8.05a4.85 4.85 0 0 1-1.47-1.36Z"/>
  </svg>
);

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [unread, setUnread] = useState(0);
  const { user, login, logout } = useAuth();

  useEffect(() => {
    api.get("/site_content").then(r => setContent(r.data)).catch(() => setContent({}));
  }, []);

  // Poll unread contact messages for admins (every 45s)
  useEffect(() => {
    if (!user?.is_admin) { setUnread(0); return; }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/admin/contact_messages");
        if (!cancelled) setUnread((data || []).filter(m => !m.read).length);
      } catch {}
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 45000);
    const onFocus = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user?.is_admin]);

  const c = content || {};
  const logo = c.logo_url;

  const socials = [
    { k: "instagram", url: c.social_instagram, Icon: InstagramColorIcon, label: "Instagram", brand: true },
    { k: "facebook",  url: c.social_facebook,  Icon: Facebook,  label: "Facebook" },
    { k: "tiktok",    url: c.social_tiktok,    Icon: TikTokIcon, label: "TikTok" },
    { k: "youtube",   url: c.social_youtube,   Icon: Youtube,   label: "YouTube" },
    { k: "twitter",   url: c.social_twitter,   Icon: XIcon,     label: "X (Twitter)" },
    { k: "spotify",   url: c.social_spotify,   Icon: Music2,    label: "Spotify" },
  ].filter(s => s.url && s.url.trim());

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-50 border-b border-white/40 bg-black/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
            {logo ? (
              <img src={logo} alt="Flow.Bologna" className="h-16 sm:h-20 max-h-20 w-auto object-contain" data-testid="brand-logo"/>
            ) : (
              <>
                <div className="flex h-9 w-9 items-center justify-center border border-white text-white font-black text-lg group-hover:rotate-3 transition-transform">F</div>
                <div className="font-display text-2xl leading-none text-white">
                  FLOW<span className="text-white">.</span>BOLOGNA
                </div>
              </>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map(({ to, label, end }) => (
              <NavLink key={to} to={to} end={end}
                data-testid={`nav-${label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-semibold uppercase tracking-wide transition-colors ${
                    isActive ? "text-white underline underline-offset-8" : "text-white/70 hover:text-white"
                  }`}>
                {label}
              </NavLink>
            ))}
            {user?.is_admin && (
              <NavLink to="/admin" data-testid="nav-admin"
                className={({ isActive }) =>
                  `ml-2 px-3 py-2 text-sm font-semibold uppercase tracking-wide flex items-center gap-1 relative ${
                    isActive ? "text-white underline underline-offset-8" : "text-white/70 hover:text-white"
                  }`}>
                <ShieldCheck size={14} /> Admin
                {unread > 0 && (
                  <span data-testid="nav-admin-badge"
                    className="ml-1 min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-600 text-white text-[11px] font-bold rounded-full font-mono leading-none shadow-[0_0_0_2px_rgba(0,0,0,0.9)] animate-pulse">
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-xs text-white/70 max-w-[140px] truncate" data-testid="user-email">{user.email}</span>
                <button data-testid="logout-btn" onClick={logout} className="brutal-btn brutal-btn-ghost text-xs px-3 py-2">
                  <LogOut size={14}/> Esci
                </button>
              </div>
            ) : (
              <button data-testid="login-btn" onClick={login} className="brutal-btn brutal-btn-outline text-xs px-3 py-2">
                <LogIn size={14}/> Admin Login
              </button>
            )}
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}
              className="lg:hidden flex h-10 w-10 items-center justify-center border border-white text-white relative">
              {open ? <X size={18}/> : <Menu size={18}/>}
              {!open && user?.is_admin && unread > 0 && (
                <span data-testid="mobile-toggle-dot"
                  className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-600 rounded-full border-2 border-black animate-pulse"/>
              )}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/40 bg-black" data-testid="mobile-drawer">
            <div className="px-4 py-3 flex flex-col gap-1">
              {NAV.map(({ to, label, icon: Icon, end }) => (
                <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
                  data-testid={`mnav-${label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 text-sm font-semibold uppercase ${
                      isActive ? "text-white bg-white/10" : "text-white"
                    }`}>
                  <Icon size={16}/> {label}
                </NavLink>
              ))}
              {user?.is_admin && (
                <NavLink to="/admin" onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 text-sm font-semibold uppercase text-white relative">
                  <ShieldCheck size={16}/> Admin
                  {unread > 0 && (
                    <span data-testid="mnav-admin-badge"
                      className="ml-auto min-w-[22px] h-[22px] px-1.5 flex items-center justify-center bg-red-600 text-white text-xs font-bold rounded-full font-mono">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </NavLink>
              )}
              {user ? (
                <button onClick={() => { logout(); setOpen(false); }} className="mt-2 brutal-btn brutal-btn-ghost">
                  <LogOut size={14}/> Esci ({user.email})
                </button>
              ) : (
                <button onClick={() => { login(); }} className="mt-2 brutal-btn brutal-btn-outline">
                  <LogIn size={14}/> Admin Login
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="relative">
        <Outlet context={{ content }}/>
      </main>

      <footer className="mt-24 border-t border-white/40 bg-black">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-10">
            {/* Brand + address */}
            <div>
              {logo ? (
                <img src={logo} alt="Flow.Bologna" className="h-14 max-h-14 w-auto object-contain"/>
              ) : (
                <div className="font-display text-3xl text-white">FLOW<span className="text-white">.</span>BOLOGNA</div>
              )}
              <p className="mt-4 text-white/80 text-sm whitespace-pre-line" data-testid="footer-text">
                {c.footer_text || "Flow Bologna · Via Cesare Boldrini 12"}
              </p>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-display text-xl uppercase text-white">Contattaci</h4>
              {c.contact_email ? (
                <a href={`mailto:${c.contact_email}`} data-testid="footer-email"
                  className="mt-3 inline-flex items-center gap-2 text-white/80 hover:text-white text-sm">
                  <Mail size={14}/> {c.contact_email}
                </a>
              ) : (
                <div className="mt-3 text-white/50 text-sm">Email non impostata</div>
              )}
              <Link to="/contatti" className="mt-2 block text-xs font-mono uppercase tracking-widest text-white/70 hover:text-white underline">
                Modulo contatti →
              </Link>
            </div>

            {/* Socials */}
            <div>
              <h4 className="font-display text-xl uppercase text-white">Seguici</h4>
              {socials.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {socials.map(({ k, url, Icon, label, brand }) => (
                    <a key={k} href={url} target="_blank" rel="noopener noreferrer" aria-label={label}
                      data-testid={`social-${k}`}
                      className={`w-10 h-10 flex items-center justify-center transition-colors ${
                        brand ? "hover:opacity-80" : "border border-white/60 hover:bg-white hover:text-black text-white"
                      }`}>
                      <Icon size={brand ? 28 : 18}/>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="mt-3 text-white/50 text-sm">Presto sui social</div>
              )}
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-white/20 flex items-center justify-between">
            <div className="text-xs text-white/50 font-mono uppercase tracking-widest">
              © {new Date().getFullYear()} · Flow Bologna
            </div>
            <div className="text-xs text-white/40 font-mono uppercase tracking-widest">
              DAMACO APS · ROCCA APS
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
